<template>
    <v-card :loading="saving">
        <v-card-title class="d-flex align-center">
            <v-btn icon class="mr-3" color="primary" @click="close">
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <span>{{
                isEdit ? 'Edit scope group' : 'Create scope group'
            }}</span>
        </v-card-title>
        <v-divider class="mx-4" />
        <v-card-text>
            <v-form v-model="valid" ref="form">
                <v-row class="mt-2" dense>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="form.name"
                            label="Scope group name *"
                            placeholder="e.g. Curtain Wall"
                            :rules="[rules.required]"
                            hide-details="auto"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-autocomplete
                            v-model="form.productTypeId"
                            :items="productTypes"
                            item-text="name"
                            item-value="id"
                            label="Type of scope *"
                            placeholder="Select type of scope"
                            :rules="[rules.required]"
                            hide-details="auto"
                        />
                    </v-col>
                </v-row>

                <div class="d-flex align-center mb-2 mt-4">
                    <span class="items-label">Items*</span>
                    <v-spacer />
                    <v-checkbox
                        v-model="showAssigned"
                        label="Show items already assigned"
                        hide-details
                        dense
                        class="mt-0 pt-0"
                    />
                </div>

                <div class="items-panel">
                    <div class="items-panel__search">
                        <v-text-field
                            v-model="search"
                            placeholder="Filter by name or quote"
                            prepend-inner-icon="mdi-magnify"
                            clearable
                            hide-details
                            dense
                            solo
                            flat
                            background-color="transparent"
                            class="items-panel__search-field"
                        />
                    </div>

                    <div class="items-panel__list">
                        <div
                            v-for="item in visibleItems"
                            :key="itemKey(item)"
                            class="items-panel__row"
                            :class="{
                                'items-panel__row--disabled': isLocked(item),
                                'items-panel__row--selected': isSelected(item),
                            }"
                            @click="toggleItem(item)"
                        >
                            <v-checkbox
                                :input-value="isSelected(item)"
                                :disabled="isLocked(item)"
                                hide-details
                                dense
                                class="mt-0 pt-0 shrink"
                                @click.stop.prevent="toggleItem(item)"
                            />
                            <div class="items-panel__row-main">
                                <span class="items-panel__quote">{{
                                    item.quoteNumber
                                }}</span>
                                <span class="items-panel__name">{{
                                    item.reference
                                }}</span>
                                <span
                                    v-if="assignedLabel(item)"
                                    class="items-panel__assigned"
                                >
                                    · {{ assignedLabel(item) }}
                                </span>
                            </div>
                            <span class="items-panel__price">
                                {{ formatPriceDisplay(item) }}
                            </span>
                        </div>
                        <div
                            v-if="visibleItems.length === 0"
                            class="items-panel__empty grey--text"
                        >
                            No items to show
                        </div>
                    </div>

                    <div class="items-panel__footer">
                        <span
                            >{{ selectedKeys.length }} of
                            {{ selectableCount }} items selected</span
                        >
                        <span>{{ pendingCount }} pending</span>
                    </div>
                </div>

                <div class="caption grey--text mt-2">
                    The list is never restrictively filtered: the switch above
                    also shows items already assigned to another scope.
                </div>
            </v-form>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
            <v-spacer />
            <v-btn text color="primary" @click="close">Cancel</v-btn>
            <v-btn
                color="primary"
                :disabled="!canSave"
                :loading="saving"
                @click="save"
            >
                Save
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
export default {
    name: 'CreateScopeDialog',
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        isEdit: {
            type: Boolean,
            default: false,
        },
        scope: {
            type: Object,
            default: () => null,
        },
        costs: {
            type: Array,
            default: () => [],
        },
        productTypes: {
            type: Array,
            default: () => [],
        },
        assignedKeyMap: {
            type: Object,
            default: () => ({}),
        },
        currency: {
            type: String,
            default: 'COP',
        },
        saving: {
            type: Boolean,
            default: false,
        },
        formatCurrency: {
            type: Function,
            required: true,
        },
        preselectedKeys: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            valid: false,
            showAssigned: false,
            search: '',
            selectedKeys: [],
            form: {
                name: '',
                productTypeId: null,
            },
            rules: {
                required: v => !!v || 'Required',
            },
        }
    },
    computed: {
        currentScopeId() {
            return this.scope && this.scope.id ? this.scope.id : null
        },
        pendingCount() {
            return this.costs.filter(cost => {
                const key = this.itemKey(cost)
                const owner = this.assignedKeyMap[key]
                return !owner
            }).length
        },
        selectableCount() {
            return this.costs.filter(cost => !this.isLocked(cost)).length
        },
        visibleItems() {
            const term = (this.search || '').toLowerCase().trim()
            return this.costs.filter(cost => {
                const key = this.itemKey(cost)
                const owner = this.assignedKeyMap[key]
                const isMine =
                    this.currentScopeId && owner === this.currentScopeId
                const isPending = !owner
                if (!this.showAssigned && !isPending && !isMine) {
                    return false
                }
                if (!term) {
                    return true
                }
                const haystack = `${cost.quoteNumber || ''} ${cost.reference ||
                    ''}`.toLowerCase()
                return haystack.includes(term)
            })
        },
        selectedProductType() {
            return (this.productTypes || []).find(
                type => type.id === this.form.productTypeId
            )
        },
        canSave() {
            return (
                this.valid &&
                !!this.form.name &&
                !!this.form.productTypeId &&
                this.selectedKeys.length > 0
            )
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(open) {
                if (open) {
                    this.initForm()
                }
            },
        },
    },
    methods: {
        itemKey(item) {
            return `${item.quoteId}/${item.id}`
        },
        initForm() {
            if (this.scope) {
                this.form = {
                    name: this.scope.name || '',
                    productTypeId: this.scope.productTypeId || null,
                }
                this.selectedKeys = [...(this.scope.costKeys || [])]
            } else {
                this.form = {
                    name: '',
                    productTypeId: null,
                }
                this.selectedKeys = [...(this.preselectedKeys || [])]
            }
            this.showAssigned = false
            this.search = ''
            this.$nextTick(() => {
                if (this.$refs.form) {
                    this.$refs.form.resetValidation()
                }
            })
        },
        isSelected(item) {
            return this.selectedKeys.includes(this.itemKey(item))
        },
        isLocked(item) {
            const key = this.itemKey(item)
            const owner = this.assignedKeyMap[key]
            if (!owner) {
                return false
            }
            return !(this.currentScopeId && owner === this.currentScopeId)
        },
        assignedLabel(item) {
            if (!this.isLocked(item)) {
                return ''
            }
            return 'Assigned to another scope'
        },
        formatPriceDisplay(item) {
            const currency = (item && item.currency) || this.currency || 'COP'
            return this.formatCurrency(currency, item && item.salePrice)
        },
        toggleItem(item) {
            if (this.isLocked(item)) {
                return
            }
            const key = this.itemKey(item)
            const index = this.selectedKeys.indexOf(key)
            if (index >= 0) {
                this.selectedKeys.splice(index, 1)
            } else {
                this.selectedKeys.push(key)
            }
        },
        close() {
            this.$emit('close')
        },
        save() {
            if (!this.canSave) {
                return
            }
            const productType = this.selectedProductType
            this.$emit('save', {
                id: this.currentScopeId,
                name: this.form.name.trim(),
                productTypeId: this.form.productTypeId,
                productTypeName: productType ? productType.name : '',
                costKeys: [...this.selectedKeys],
            })
        },
    },
}
</script>

<style scoped>
.items-label {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
}
.items-panel {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
    background: #fff;
}
.items-panel__search {
    background: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
    padding: 2px 4px;
}
.items-panel__search-field >>> .v-input__slot {
    min-height: 36px !important;
    box-shadow: none !important;
}
.items-panel__list {
    max-height: 280px;
    overflow-y: auto;
}
.items-panel__row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid #eeeeee;
    cursor: pointer;
}
.items-panel__row:hover {
    background: #f5f5f5;
}
.items-panel__row--selected {
    background: #f0f4f8;
}
.items-panel__row--disabled {
    opacity: 0.55;
    cursor: not-allowed;
}
.items-panel__row-main {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 6px;
}
.items-panel__quote {
    color: #9e9e9e;
    font-size: 13px;
    font-weight: 500;
}
.items-panel__name {
    color: rgba(0, 0, 0, 0.87);
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
}
.items-panel__assigned {
    color: #9e9e9e;
    font-size: 12px;
}
.items-panel__price {
    color: #9e9e9e;
    font-size: 13px;
    white-space: nowrap;
    margin-left: 8px;
}
.items-panel__empty {
    padding: 24px 12px;
    text-align: center;
}
.items-panel__footer {
    display: flex;
    justify-content: space-between;
    background: #f5f5f5;
    border-top: 1px solid #e0e0e0;
    padding: 8px 12px;
    font-size: 12px;
    color: #757575;
}
</style>
